import { useEffect, useState } from 'react';
import { Receipt as ReceiptIcon, ExternalLink, Upload, FileWarning, ShieldCheck } from 'lucide-react';
import type { Receipt } from '../../types/expense';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { ReceiptPicker } from './ReceiptPicker';
import { formatDateTime } from '../../utils/trips';
import { formatearPeso, mensajeErrorGasto } from '../../utils/expenses';

interface Props {
  receipts: Receipt[];
  isLoading: boolean;
  error: Error | null;
  subiendo: boolean;
  /** Emite la URL firmada de un recibo concreto. Es una capacidad temporal: se pide al abrir. */
  urlDeDescarga: (receiptId: string) => Promise<string>;
  /** Ausente = esta superficie es de solo lectura, o el rol no puede subir. */
  onSubir?: (file: File) => Promise<unknown>;
}

/**
 * FE-12 — el recibo de un gasto, con todas sus versiones.
 *
 * **La vigente es la primera y se pinta; las anteriores se listan.** Subir de
 * nuevo no reemplaza: crea una versión que pasa a ser la vigente, y la foto
 * equivocada sobrevive como auditoría porque el objeto es write-once (ADR-009).
 * Poder mirar el recibo original es justamente para lo que se conservan.
 *
 * ⚠️ **La URL firmada se pide al abrir, no al montar la lista.** Es una
 * capacidad temporal sobre un objeto concreto: pedir una por versión sería N+1
 * y además caducaría mientras el usuario mira la pantalla.
 *
 * ⚠️ **Un PDF se abre en una pestaña nueva, no se incrusta.** Un `<iframe>` con
 * una URL firmada de GCS depende de cabeceras del bucket que no controlamos y
 * falla en blanco; una pestaña es lo que el navegador ya sabe hacer bien.
 */
export function ReceiptViewer({ receipts, isLoading, error, subiendo, urlDeDescarga, onSubir }: Props) {
  const vigente = receipts[0];
  const anteriores = receipts.slice(1);

  const [urlVigente, setUrlVigente] = useState<string | null>(null);
  const [cargandoUrl, setCargandoUrl] = useState(false);
  const [errorUrl, setErrorUrl] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState<File | null>(null);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);

  // La vigente se resuelve sola: es la que el usuario vino a ver. Las
  // anteriores esperan a que las pida.
  useEffect(() => {
    if (!vigente || vigente.contentType === 'application/pdf') {
      setUrlVigente(null);
      return;
    }
    let cancelled = false;
    setCargandoUrl(true);
    setErrorUrl(null);
    urlDeDescarga(vigente.id)
      .then(url => { if (!cancelled) setUrlVigente(url); })
      .catch(err => { if (!cancelled) setErrorUrl(mensajeErrorGasto(err, 'recibo')); })
      .finally(() => { if (!cancelled) setCargandoUrl(false); });
    return () => { cancelled = true; };
  }, [vigente, urlDeDescarga]);

  const abrirEnPestana = async (receiptId: string) => {
    try {
      const url = await urlDeDescarga(receiptId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setErrorUrl(mensajeErrorGasto(err, 'recibo'));
    }
  };

  const subir = async () => {
    if (!nuevo || !onSubir) return;
    setErrorSubida(null);
    try {
      await onSubir(nuevo);
      setNuevo(null);
    } catch (err) {
      setErrorSubida(mensajeErrorGasto(err, 'recibo'));
    }
  };

  return (
    <div className="expenses-touch" style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <ReceiptIcon size={15} style={{ color: 'var(--accent)' }} />
        Recibo
        {receipts.length > 1 && (
          <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)' }}>
            · {receipts.length} versiones
          </span>
        )}
      </h4>

      {isLoading && <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center' }}><Spinner /></div>}

      {!isLoading && error && (
        <p style={{ margin: 0, fontSize: '12px', color: '#EF4444' }}>No se pudieron cargar los recibos: {error.message}</p>
      )}

      {!isLoading && !error && !vigente && (
        <p style={{ margin: 0, fontSize: '12px', color: '#F59E0B', display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
          <FileWarning size={14} /> Este gasto no tiene soporte contable todavía.
        </p>
      )}

      {!isLoading && !error && vigente && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {vigente.contentType === 'application/pdf' ? (
            <Button variant="secondary" size="sm" leftIcon={<ExternalLink size={14} />} onClick={() => abrirEnPestana(vigente.id)}>
              Abrir el PDF del recibo
            </Button>
          ) : cargandoUrl ? (
            <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'center' }}><Spinner /></div>
          ) : urlVigente ? (
            <a href={urlVigente} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
              <img
                src={urlVigente}
                alt="Recibo del gasto"
                style={{ width: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'var(--bg-raised)' }}
              />
            </a>
          ) : null}

          {errorUrl && <p style={{ margin: 0, fontSize: '12px', color: '#EF4444' }}>{errorUrl}</p>}

          <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <span>{formatDateTime(vigente.uploadedAt)}</span>
            <span>· {formatearPeso(vigente.sizeBytes)}</span>
            <span title={`SHA-256: ${vigente.integrityHash}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <ShieldCheck size={12} /> {vigente.integrityHash.slice(0, 12)}…
            </span>
          </div>
        </div>
      )}

      {anteriores.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            Versiones anteriores
          </p>
          {anteriores.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '11.5px', color: 'var(--text-secondary)' }}>
              <span>{formatDateTime(r.uploadedAt)}</span>
              <span style={{ color: 'var(--text-muted)' }}>· {formatearPeso(r.sizeBytes)}</span>
              <Button size="sm" variant="ghost" leftIcon={<ExternalLink size={12} />} onClick={() => abrirEnPestana(r.id)}>
                Ver
              </Button>
            </div>
          ))}
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
            No se pueden borrar: una foto sustituida sigue siendo parte de la auditoría.
          </p>
        </div>
      )}

      {onSubir && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.25rem', borderTop: '1px solid var(--border)' }}>
          <ReceiptPicker file={nuevo} onChange={setNuevo} disabled={subiendo} />
          {nuevo && (
            <Button size="sm" leftIcon={<Upload size={14} />} isLoading={subiendo} onClick={subir}>
              {vigente ? 'Subir como versión nueva' : 'Adjuntar recibo'}
            </Button>
          )}
          {errorSubida && <p style={{ margin: 0, fontSize: '12px', color: '#EF4444' }}>{errorSubida}</p>}
        </div>
      )}
    </div>
  );
}
